import React from "react";
import { Building2, MessageCircle, MailCheck, CheckCircle2, Settings2, Eye, Award } from "lucide-react";

const TIER_CHIP = {
  ouro:   "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]",
  prata:  "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900",
  bronze: "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white",
};

const isCertActive = (c) => {
  if (!c?.certification_tier) return false;
  if (!["paid", "waived"].includes((c.certification_payment_status || "").toLowerCase())) return false;
  if (!c.certification_expires_at) return false;
  return new Date(c.certification_expires_at) > new Date();
};

export const CaseClinicsList = ({
  caseData, clinics, templates, selectedTpl, setSelectedTpl,
  onOpenTemplates, onWhatsapp, onReserve, onViewDetails,
}) => {
  const c = caseData;
  const [onlyCertified, setOnlyCertified] = React.useState(false);
  const certifiedCount = (clinics || []).filter(isCertActive).length;
  const visibleClinics = onlyCertified
    ? (clinics || []).filter(isCertActive)
    : (clinics || []);
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display font-bold text-xl text-[#0B2B62] flex items-center gap-2">
          <Building2 className="w-5 h-5" /> Clínicas compatíveis
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none"
            data-testid="clinics-only-certified-toggle">
            <input type="checkbox" checked={onlyCertified} onChange={(e) => setOnlyCertified(e.target.checked)}
              className="accent-amber-600" />
            <Award className="w-3 h-3 text-amber-600" />
            Só credenciadas ({certifiedCount}/{(clinics || []).length})
          </label>
          {c.clinic_id && (
            <div className="flex items-center gap-1.5">
              <select data-testid="template-select" value={selectedTpl} onChange={(e) => setSelectedTpl(e.target.value)}
                className="text-xs px-2 py-1.5 border border-slate-300 rounded-md bg-white outline-none">
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button data-testid="manage-templates" onClick={onOpenTemplates} title="Gerenciar templates"
                className="p-1.5 rounded-md hover:bg-slate-50 text-slate-500">
                <Settings2 className="w-4 h-4" />
              </button>
              <button data-testid="whatsapp-btn" onClick={onWhatsapp}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1DA851] transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
            </div>
          )}
        </div>
      </div>
      {c.clinic_email_sent_at && (
        <div className="mb-3 p-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-600 flex items-center gap-2" data-testid="clinic-email-status">
          <MailCheck className="w-3.5 h-3.5 text-slate-500" />
          DIR enviado por e-mail a <b className="ml-1">{c.clinic_email_to}</b> em {new Date(c.clinic_email_sent_at).toLocaleString("pt-BR")}
          {c.clinic_email_read_at ? (
            <span className="ml-auto inline-flex items-center gap-1 text-green-700 font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Lido em {new Date(c.clinic_email_read_at).toLocaleString("pt-BR")}
            </span>
          ) : (
            <span className="ml-auto text-slate-500">Aguardando leitura</span>
          )}
        </div>
      )}
      <div className="space-y-3">
        {visibleClinics.map((cl) => (
          <div key={cl.id} className="p-4 border border-slate-200 rounded-lg flex items-start justify-between gap-3 hover:border-[#0B2B62] transition-colors">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-semibold text-[#0B2B62]">{cl.name}</h3>
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-mono">match {cl.match_score}%</span>
                {isCertActive(cl) && (
                  <span data-testid={`clinic-tier-badge-${cl.id}`}
                    className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest inline-flex items-center gap-0.5 ${TIER_CHIP[cl.certification_tier] || ""}`}>
                    <Award className="w-2.5 h-2.5" /> Credenciada · {cl.certification_tier}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{cl.city}, {cl.state} · {cl.beds_available} vaga(s)</div>
              <div className="mt-1.5 text-xs text-slate-600">{cl.match_reasons.join(" · ")}</div>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              {onViewDetails && (
                <button data-testid={`view-details-${cl.id}`} onClick={() => onViewDetails(cl.id)}
                  className="inline-flex items-center justify-center gap-1 px-3 py-1.5 border border-slate-300 text-slate-700 rounded-md text-sm hover:bg-slate-50 transition-colors">
                  <Eye className="w-3.5 h-3.5" /> Ver detalhes
                </button>
              )}
              <button data-testid={`reserve-${cl.id}`} onClick={() => onReserve(cl.id)} disabled={cl.beds_available === 0}
                className="px-3 py-1.5 bg-[#0B2B62] text-white rounded-md text-sm hover:bg-slate-800 disabled:opacity-40 transition-colors">
                {c.clinic_id === cl.id ? "Reservada ✓" : "Reservar vaga"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
